import {useEffect, useState} from 'react';
import {ArrowRight} from 'lucide-react';
import {ConfirmationModal} from '@/matic/features/_shared/components/ConfirmationModal';
import {Input} from '@/matic/components/ui/input';
import {useToast} from '@/matic/components/ui/toast';
import {
  extractValueChangedDetail,
  isWriteInFlightError,
} from '../api/erp-record-field-write-api';
import {
  useErpFieldWriteCommit,
  useErpFieldWritePreview,
} from '../hooks/useErpFieldWrite';
import type {ErpFieldWritePreview} from '../types';
import {displayErpValue as displayValue} from '../utils';

type Props = {
  open: boolean;
  objectRecordId: string;
  typeId?: string;
  fieldKey: string;
  fieldLabel: string;
  /** Boolean fields get a two-way toggle; everything else is a text input. */
  fieldType?: string;
  /** MATIC's current value, used to seed the editor. */
  initialValue: unknown;
  sourceLabel: string;
  /**
   * The confirmed value, handed back so the form the user is looking at
   * updates in place. Cache invalidation alone doesn't cover this: the form
   * seeds its state once at mount, so a refetch behind it leaves the visible
   * field showing the pre-write value until the whole surface remounts.
   */
  onWritten?: (fieldKey: string, value: unknown) => void;
  onClose: () => void;
};

/** Editor-safe text for the input, where an em-dash placeholder would be wrong. */
function editableText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value.toString();
  }

  return JSON.stringify(value) ?? '';
}

/** The single-field editor shown before the diff is fetched. */
function FieldEditor({
  fieldLabel,
  isBoolean,
  draft,
  onChange,
}: {
  readonly fieldLabel: string;
  readonly isBoolean: boolean;
  readonly draft: unknown;
  readonly onChange: (value: unknown) => void;
}) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-neutral-600">
        {fieldLabel}
      </span>
      {isBoolean ? (
        <select
          className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm text-neutral-800"
          value={draft === true ? 'true' : 'false'}
          onChange={(event) => {
            onChange(event.target.value === 'true');
          }}
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      ) : (
        <Input
          value={editableText(draft)}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
    </div>
  );
}

/**
 * The resolved `current → proposed` diff.
 *
 * The "current" side is the ERP's live value, never MATIC's projection —
 * that distinction is the whole reason preview costs a metered call.
 */
function WriteDiff({
  preview,
  sourceLabel,
}: {
  readonly preview: ErpFieldWritePreview;
  readonly sourceLabel: string;
}) {
  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
      <p className="mb-1.5 text-xs font-medium text-neutral-500">
        {preview.isFirstFill
          ? `Adding to ${sourceLabel}`
          : `Changing in ${sourceLabel}`}
      </p>
      <div className="flex items-center gap-2 text-xs text-neutral-600">
        <span className="shrink-0 text-neutral-500">{preview.label}</span>
        <span className="truncate">
          {displayValue(preview.currentErpValue)}
        </span>
        <ArrowRight
          className="h-3 w-3 shrink-0 text-neutral-400"
          aria-hidden="true"
        />
        <span className="truncate font-medium text-neutral-900">
          {displayValue(preview.proposedValue)}
        </span>
      </div>
    </div>
  );
}

/**
 * Edit one ERP-fed field and write it back to the ERP.
 *
 * Two steps on purpose. The user types a value, then confirms against a
 * diff built from a **live read of the ERP** — not from MATIC's
 * projection, which is only as fresh as the last manual sync and can show
 * a "before" that stopped being true weeks ago.
 *
 * The 409 path is handled in place rather than as a toast: if the ERP
 * moved while the dialog was open, the diff re-renders against the real
 * current value and the user decides again. Putting them back in front of
 * the decision with correct data is the entire point of the check, so
 * closing the dialog to show an error would defeat it.
 */
export function CetecFieldWriteDialog({
  open,
  objectRecordId,
  typeId,
  fieldKey,
  fieldLabel,
  fieldType,
  initialValue,
  sourceLabel,
  onWritten,
  onClose,
}: Props) {
  const {toast} = useToast();
  const isBoolean = fieldType === 'boolean';
  const [draft, setDraft] = useState<unknown>(initialValue ?? '');
  const [preview, setPreview] = useState<ErpFieldWritePreview | undefined>();
  const [conflictMessage, setConflictMessage] = useState<string | undefined>();

  const previewMutation = useErpFieldWritePreview();
  const commitMutation = useErpFieldWriteCommit(typeId);

  // Reset every time the dialog opens: a stale draft or a stale preview
  // from a previous field would be confirmed against the wrong diff.
  useEffect(() => {
    if (!open) return;
    setDraft(initialValue ?? '');
    setPreview(undefined);
    setConflictMessage(undefined);
  }, [open, initialValue, fieldKey]);

  const requestPreview = async () => {
    setConflictMessage(undefined);
    try {
      // A batch of one: the dialog edits a single field, but the contract
      // is the same list shape the ledger card's multi-field confirm uses.
      const [result] = await previewMutation.mutateAsync({
        objectRecordId,
        changes: [{fieldKey, proposedValue: draft}],
      });
      setPreview(result);
    } catch (error) {
      toast({
        title: `Couldn't reach ${sourceLabel}`,
        description: (error as Error).message,
        variant: 'error',
      });
    }
  };

  const confirm = async () => {
    if (!preview) return;
    try {
      const result = await commitMutation.mutateAsync({
        objectRecordId,
        changes: [
          {
            fieldKey,
            proposedValue: preview.proposedValue,
            expectedCurrentValue: preview.currentErpValue,
          },
        ],
        // Per attempt, never per dialog-open: a re-confirm after a 409 is a
        // new decision, and reusing the key would replay the old outcome.
        idempotencyKey: crypto.randomUUID(),
      });
      // Exactly one field went out, so exactly one outcome can come back.
      // An empty `written` means it didn't land — the server raises rather
      // than returning when nothing was written, so this is belt-and-braces.
      const outcome = result.written[0];

      if (result.localUpdateFailed) {
        // The authoritative write landed — say so, and don't invite a retry.
        toast({
          title: `Saved in ${sourceLabel}`,
          description: `MATIC's copy couldn't be updated (${
            result.localUpdateError ?? 'validation failed'
          }). It'll refresh on the next sync.`,
          variant: 'warning',
        });
        // Deliberately no onWritten here: the ERP moved but MATIC's copy did
        // not, so painting the new value into the form would assert a local
        // state that isn't in the database. The stale value is the honest one
        // until the next sync reconciles it.
      } else if (outcome) {
        onWritten?.(fieldKey, outcome.writtenValue);
        toast({
          title: outcome.wasFirstFill
            ? `Added in ${sourceLabel}`
            : `Updated in ${sourceLabel}`,
          description: `${fieldLabel} is now ${displayValue(outcome.writtenValue)}.`,
          variant: 'success',
        });
      } else {
        // Belt-and-braces: the field landed in neither `written` nor as a
        // reported conflict/unverified row. The backend contract says this
        // shouldn't happen for a single-field commit, but failing silently
        // here would leave the user believing nothing went wrong.
        toast({
          title: `Couldn't confirm the save to ${sourceLabel}`,
          description:
            'The change may or may not have landed — reopen the field to check its current value.',
          variant: 'error',
        });
      }

      onClose();
    } catch (error) {
      const changed = extractValueChangedDetail(error);
      if (changed) {
        // Re-render the diff against what the ERP actually holds now,
        // rather than closing with an error the user can't act on.
        setPreview((current) =>
          current
            ? {
                ...current,
                currentErpValue: changed.currentErpValue,
                isFirstFill:
                  changed.currentErpValue === null ||
                  changed.currentErpValue === undefined ||
                  changed.currentErpValue === '',
              }
            : current
        );
        setConflictMessage(
          `This changed in ${sourceLabel} while you were reviewing. Here's the current value — confirm again to overwrite it.`
        );
        return;
      }

      if (isWriteInFlightError(error)) {
        setConflictMessage(
          'This change is already being saved. Give it a moment before trying again.'
        );
        return;
      }

      toast({
        title: `Couldn't save to ${sourceLabel}`,
        description: (error as Error).message,
        variant: 'error',
      });
    }
  };

  const isConfirmStep = Boolean(preview);
  const isFirstFill = preview?.isFirstFill ?? false;
  // A stale projection is news in its own right — it means what the record
  // has been showing was wrong, which changes what the user is agreeing to.
  const projectionIsStale =
    preview !== undefined &&
    displayValue(preview.currentErpValue) !==
      displayValue(preview.currentLocalValue);

  return (
    <ConfirmationModal
      open={open}
      title={
        isConfirmStep
          ? isFirstFill
            ? `Add to ${sourceLabel}`
            : `Update in ${sourceLabel}`
          : `Edit ${fieldLabel}`
      }
      description={
        isConfirmStep
          ? `${sourceLabel} is the system of record for this field. Confirming writes the value there, then refreshes MATIC's copy.`
          : `This field is fed by ${sourceLabel}. Your edit is written back to ${sourceLabel}, so it sticks.`
      }
      confirmText={
        isConfirmStep ? (isFirstFill ? 'Add' : 'Update') : 'Review change'
      }
      cancelText="Cancel"
      variant="primary"
      isLoading={previewMutation.isPending || commitMutation.isPending}
      onConfirm={isConfirmStep ? confirm : requestPreview}
      onCancel={onClose}
      extraContent={
        <div className="space-y-3">
          {isConfirmStep ? undefined : (
            <FieldEditor
              fieldLabel={fieldLabel}
              isBoolean={isBoolean}
              draft={draft}
              onChange={setDraft}
            />
          )}

          {preview ? (
            <WriteDiff preview={preview} sourceLabel={sourceLabel} />
          ) : undefined}

          {preview?.isNoOp ? (
            <p className="text-xs text-neutral-500">
              {sourceLabel} already has this value — there&apos;s nothing to
              change.
            </p>
          ) : undefined}

          {projectionIsStale ? (
            <p className="text-xs text-warning-700">
              MATIC was showing{' '}
              <span className="font-medium">
                {displayValue(preview?.currentLocalValue)}
              </span>
              , but {sourceLabel} currently has{' '}
              <span className="font-medium">
                {displayValue(preview?.currentErpValue)}
              </span>
              . You&apos;re replacing {sourceLabel}&apos;s value.
            </p>
          ) : undefined}

          {conflictMessage ? (
            <p className="text-xs text-warning-700">{conflictMessage}</p>
          ) : undefined}
        </div>
      }
    />
  );
}
